import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import cuid from 'cuid';
import { useSnackbar } from 'notistack';
import { documentTypeTaxonomy, type DocumentTypeTaxonomyEntry } from '@lio/rule-engine';
import { Dialog } from '@components/dialog';
import { useCreateDocument, useDeleteDocument } from '@services/document';
import { uploadFile, useCreateFile, useDeleteFile } from '@services/file';
import { ToastType } from '@components/toast';
import { DocumentUploadView } from '@components/dialog/DocumentUploadView';
import { useDocumentFileSelection } from '@hooks/useDocumentFileSelection';

interface UploadDocumentDialogProps {
  open: boolean;
  policyId: string;
  isInternalUser: boolean;
  onClose: () => void;
}

export const UploadDocumentDialog: FC<UploadDocumentDialogProps> = ({ open, policyId, isInternalUser, onClose }) => {
  const [isUploading, setIsUploading] = useState(false);
  const { createFileAsync } = useCreateFile();
  const { createDocumentAsync } = useCreateDocument();
  const { deleteFileAsync } = useDeleteFile();
  const { deleteDocumentAsync } = useDeleteDocument();
  const { enqueueSnackbar } = useSnackbar();

  const uploadableTypes = useMemo(
    () =>
      ((documentTypeTaxonomy as DocumentTypeTaxonomyEntry[] | undefined) ?? []).filter((e) =>
        isInternalUser ? e.internalUpload : e.brokerUpload,
      ),
    [isInternalUser],
  );

  const {
    files,
    dragging,
    handleFileInputChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    updateEntry,
    removeFile,
    reset,
    totalSize,
    validFiles,
  } = useDocumentFileSelection('');

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setIsUploading(true);

    try {
      const results = await Promise.all(
        files.map(async ({ artifact, documentTypeId, description }) => {
          if (artifact.invalidMsg) {
            return false;
          }

          let createdFileId: string | undefined;
          let createdDocumentId: string | undefined;

          try {
            const awsS3Id = `document/${cuid()}`;
            const createdFile = await createFileAsync({
              description: description || undefined,
              awsS3Id,
              fileName: artifact.name,
              originalFileName: artifact.name,
              fileType: artifact.mimetype,
            });
            createdFileId = createdFile.id;

            if (!createdFile.uploadUrl) throw new Error('No upload URL received');

            await uploadFile(createdFile.uploadUrl, artifact.file);

            const createdDoc = await createDocumentAsync({
              documentTypeId,
              fileId: createdFile.id,
              policyId,
            });
            createdDocumentId = createdDoc.id;

            return true;
          } catch (e) {
            await Promise.allSettled([
              createdDocumentId ? deleteDocumentAsync({ documentId: createdDocumentId }) : Promise.resolve(),
              createdFileId ? deleteFileAsync({ fileId: createdFileId }) : Promise.resolve(),
            ]);
            return false;
          }
        }),
      );

      const successCount = results.filter(Boolean).length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        enqueueSnackbar(`${successCount} document${successCount !== 1 ? 's' : ''} uploaded successfully`, {
          variant: ToastType.Success,
        });
      }
      if (failCount > 0) {
        enqueueSnackbar(`${failCount} document${failCount !== 1 ? 's' : ''} failed to upload`, {
          variant: ToastType.Error,
        });
      }
      handleClose();
    } finally {
      setIsUploading(false);
    }
  };

  const isSubmitDisabled = validFiles.length === 0 || uploadableTypes.length === 0;

  return (
    <Dialog
      open={open}
      size='large'
      title='Upload Documents'
      handleCloseDialog={handleClose}
      primaryBtnText='Upload'
      primaryBtnAction={handleSubmit}
      isPrimaryBtnDisabled={isSubmitDisabled}
      isPrimaryBtnLoading={isUploading}
      isSecondaryBtnDisabled={isUploading}
      secondaryBtnText='Cancel'
      secondaryBtnAction={handleClose}
      sx={(theme) => ({
        '& .MuiDialogContent-root': {
          backgroundColor:
            theme.palette.theme === 'lio' ? theme.palette.background.main : `${theme.palette.background.main}20`,
        },
      })}
    >
      <Grid container sx={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
        <DocumentUploadView
          files={files}
          dragging={dragging}
          uploadableTypes={uploadableTypes}
          onFileInputChange={handleFileInputChange}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onUpdateEntry={updateEntry}
          onRemoveFile={removeFile}
          fileInputId='upload-document-input'
          totalSize={totalSize}
        />
      </Grid>
    </Dialog>
  );
};
